// IFRS 18 and Cash Flow Fixes Card V2 · Part A — the statement of profit or loss (and other comprehensive income) and the new
// note parts for IFRS.jsx. That page is over 1,500 lines: new code lives here (the project's frozen-files rule, section 7). Moved here from IFRS.jsx:
// PLRenderer (unchanged rows and test ids), now with A2 discontinued operations, A4 the IFRS 18 order (each category, then its
// subtotal), A5 other comprehensive income; plus the A9 first-year toggle and transition note, and the Arabic titles of the
// new notes.
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import { RestatedTag } from "@/components/restatement/RestatedStatements";   // Restatement Phase 3a: "Restated" on the comparative
import { asArray } from "@/lib/asArray";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const fmt = (n) => fmtNumber(Number(n ?? 0));

// A6 / A7 / A9: the new notes carry an Arabic title (title_ar) for the note and its lines
// IFRS 18 / CF card V2 · screenshot finding: a year with no discontinued line has no stored continuing subtotal — its
// continuing profit is its profit for the period (the prior year printed 0.00 next to a current year with a disposal)
export function continuingProfit(pl) {
  const st = pl?.subtotals || {};
  if (st.profit_from_continuing_operations !== undefined && st.profit_from_continuing_operations !== null) {
    return st.profit_from_continuing_operations;
  }
  return Number(pl?.profit_for_period || 0) - Number(st.discontinued_operations || 0);
}

export function noteTitle(n, lang) {
  return (lang === "ar" && n?.title_ar) || n?.title || "";
}

const IFRS18_ORDER = [
  ["operating", "operating_profit", "ifrs.plOperatingProfit", "pl-sub-operating"],
  ["investing", "profit_before_financing_and_tax", "ifrs.a18.beforeFinTax", "pl-sub-before-fin-tax"],
  ["financing", "profit_before_tax", "ifrs.plBeforeTax", "pl-sub-before-tax"],
  ["income_taxes", null, null, null],
];

export function PLRenderer({ pl, engagement, onOpenMapping, FrameworkBadge, UnmappedInlineWarning }) {
  const { t, lang } = useI18n();
  if (!pl) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.plLoading")}</CardContent></Card>;
  }
  const c = pl.current;
  const p = pl.prior;
  const framework = c.framework;
  const hasOci = !!(c.oci || p?.oci);
  const hasDisc = c.subtotals?.discontinued_operations !== undefined || p?.subtotals?.discontinued_operations !== undefined;
  const catLabel = (k, cat) => { const v = t(`ifrs.a18.cat.${k}`); return v && v !== `ifrs.a18.cat.${k}` ? v : cat?.label; };

  // IFRS 18 / CF card V2 · A3: an FX line split by tag (fx_tag) is matched on code + tag
  const priorFor = (code, fxTag) => {
    if (!p) return null;
    if (framework === "IFRS 18") {
      for (const cat of Object.values(p.categories || {})) {
        const f = asArray(cat.lines).find((l) => l.code === code && (l.fx_tag || "") === (fxTag || ""));
        if (f) return f.profit_contribution;
      }
      return 0;
    }
    const f = asArray(p.lines).find((l) => l.code === code);
    return f ? f.profit_contribution : 0;
  };

  // PR 46 review · A6 on screen: a line found only in the prior year is shown with 0.00 for the current year, so each
  // category's lines add up to its total in both columns (as in the PDF)
  const withPriorOnly = (cat, priorCat) => {
    const cur = asArray(cat?.lines);
    const key = (l) => `${l.code}|${l.fx_tag || ""}`;
    const seen = new Set(cur.map(key));
    const extra = asArray(priorCat?.lines).filter((l) => !seen.has(key(l)))
      .map((l) => ({ ...l, profit_contribution: 0 }));
    return [...cur, ...extra];
  };

  const renderLineRow = (l) => (
    <div key={`${l.code}${l.fx_tag || ""}`} className="grid grid-cols-2 border-b px-4 py-1.5 text-sm">
      <div className="text-slate-700 dark:text-slate-300">
        <span className="me-2 font-mono text-[10px] text-muted-foreground">{l.code}</span>
        {(lang === "ar" && l.name_ar) || l.name}
      </div>
      <div className="grid grid-cols-2 gap-4 text-end num-cell">
        <div>{fmt(l.profit_contribution)}</div>
        <div className="text-muted-foreground">{fmt(priorFor(l.code, l.fx_tag))}</div>
      </div>
    </div>
  );

  const subtotal = (label, value, priorValue, testId) => (
    <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 dark:bg-slate-900/40"
         data-testid={testId}>
      <div className="text-sm font-semibold">{label}</div>
      <div className="grid grid-cols-2 gap-4 text-end num-cell font-bold">
        <div>{fmt(value)}</div>
        <div className="text-muted-foreground">{fmt(priorValue)}</div>
      </div>
    </div>
  );

  // A2: one after-tax amount for discontinued operations, with the note lines (result / gain on disposal) under it
  const discontinuedBlock = () => {
    if (!hasDisc) return null;
    const pb = asArray(p?.discontinued_breakdown);
    return (
      <div data-testid="pl-discontinued-block">
        {subtotal(t("ifrs.a18.continuing"), continuingProfit(c), p ? continuingProfit(p) : 0, "pl-sub-continuing")}
        <div className="grid grid-cols-2 border-b px-4 py-1.5 text-sm" data-testid="pl-discontinued">
          <div className="text-slate-700 dark:text-slate-300">
            <span className="me-2 font-mono text-[10px] text-muted-foreground">5901</span>{t("ifrs.a18.discontinued")}
          </div>
          <div className="grid grid-cols-2 gap-4 text-end num-cell">
            <div>{fmt(c.subtotals.discontinued_operations ?? 0)}</div>
            <div className="text-muted-foreground">{fmt(p?.subtotals?.discontinued_operations || 0)}</div>
          </div>
        </div>
        {asArray(c.discontinued_breakdown).map((b) => (
          <div key={b.code} className="grid grid-cols-2 border-b px-4 py-1 text-[11px] text-muted-foreground"
               data-testid={`pl-discontinued-${b.code}`}>
            <div className="ps-6">
              {t("ifrs.a18.discontinuedOfWhich").replace("{title}", (lang === "ar" && b.title_ar) || b.title)}
              {b.note_no ? <span className="ms-1 font-mono text-[10px]">#{b.note_no}</span> : null}
            </div>
            <div className="grid grid-cols-2 gap-4 text-end num-cell">
              <div>{fmt(b.amount)}</div>
              <div>{fmt((pb.find((x) => x.code === b.code) || {}).amount || 0)}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const title = framework === "IFRS 18"
    ? t(hasOci ? "ifrs.a18.plTitleOciIfrs18" : "ifrs.plTitleIfrs18")
    : t(hasOci ? "ifrs.a18.plTitleOciIas1" : "ifrs.plTitleIas1");

  return (
    <Card data-testid="pl-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          {engagement.client_name} — {title} · FY{pl.financial_year}
        </CardTitle>
        {FrameworkBadge && <FrameworkBadge framework={framework} />}
      </CardHeader>
      <CardContent className="p-0">
        {UnmappedInlineWarning && <UnmappedInlineWarning list={c.unmapped_accounts} onOpenMapping={onOpenMapping} />}
        <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
          <div>{t("ifrs.item")}</div>
          <div className="grid grid-cols-2 gap-4 text-end">
            <div>FY{pl.financial_year}</div>
            <div>FY{Number(pl.financial_year) - 1}<RestatedTag /></div>
          </div>
        </div>

        {framework === "IFRS 18" && (
          <>
            {/* A4: each category with its total, then the subtotal IFRS 18 requires after it */}
            {IFRS18_ORDER.map(([k, subKey, subLabel, subTestId]) => {
              const cat = c.categories[k];
              const priorCat = p?.categories?.[k];
              const rows = withPriorOnly(cat, priorCat);
              const shown = rows.length > 0;
              return (
                <React.Fragment key={k}>
                  {shown && (
                    <div data-testid={`pl-cat-${k}`}>
                      <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                        {catLabel(k, cat || priorCat)}
                      </div>
                      {rows.map(renderLineRow)}
                      {subtotal(`${t("ifrs.totalPrefix")}${catLabel(k, cat || priorCat)}`, cat?.total || 0, priorCat?.total || 0,
                                  `pl-cat-total-${k}`)}
                    </div>
                  )}
                  {subKey && subtotal(t(subLabel), c.subtotals[subKey], p?.subtotals?.[subKey] || 0, subTestId)}
                </React.Fragment>
              );
            })}
            {discontinuedBlock()}
          </>
        )}

        {framework === "IAS 1" && (
          <>
            {/* Batch 6 · E (AT-60): Revenue = 4101 only; other operating income (4102 …) sits below gross profit */}
            {asArray(c.lines).filter((l) => l.code === "4101").map(renderLineRow)}
            {subtotal(t("ifrs.plRevenue"), c.subtotals.revenue, p?.subtotals?.revenue || 0, "pl-sub-revenue")}
            {asArray(c.lines).filter((l) => l.code === "5101").map(renderLineRow)}
            {subtotal(t("ifrs.plGrossProfit"), c.subtotals.gross_profit,
                       p?.subtotals?.gross_profit || 0, "pl-sub-gross")}
            {asArray(c.lines).filter((l) => l.category === "OP" && l.code[0] === "4" && l.code !== "4101").map(renderLineRow)}
            {asArray(c.lines).some((l) => l.category === "OP" && l.code[0] === "4" && l.code !== "4101") &&
              subtotal(t("ifrs.plOtherOpIncome"), c.subtotals.other_operating_income || 0, p?.subtotals?.other_operating_income || 0, "pl-sub-other-income")}
            {asArray(c.lines).filter((l) => l.category === "OP" && l.code[0] === "5" && l.code !== "5101").map(renderLineRow)}
            {subtotal(t("ifrs.plOperatingProfit"), c.subtotals.operating_profit,
                       p?.subtotals?.operating_profit || 0, "pl-sub-operating")}
            {asArray(c.lines).filter((l) => l.category === "INV" || l.category === "FIN").map(renderLineRow)}
            {subtotal(t("ifrs.plBeforeTax"), c.subtotals.profit_before_tax,
                       p?.subtotals?.profit_before_tax || 0, "pl-sub-before-tax")}
            {asArray(c.lines).filter((l) => l.category === "TAX").map(renderLineRow)}
            {discontinuedBlock()}
          </>
        )}

        <div className="grid grid-cols-2 bg-slate-900 px-4 py-3 text-white" data-testid="pl-profit-for-period">
          <div className="text-sm font-semibold">{t("ifrs.plProfitForPeriod")}</div>
          <div className="grid grid-cols-2 gap-4 text-end num-cell font-bold">
            <div>{fmt(c.profit_for_period)}</div>
            <div className="text-slate-300">{fmt(p?.profit_for_period || 0)}</div>
          </div>
        </div>
        {hasOci && <OciSection current={c} prior={p} subtotal={subtotal} />}
      </CardContent>
    </Card>
  );
}

// A5: other comprehensive income — the two groups, each line net of its own tax, OCI for the year, total comprehensive income
export function OciSection({ current, prior, subtotal }) {
  const { t, lang } = useI18n();
  const oci = current?.oci || { groups: [], total: 0 };
  const poci = prior?.oci || { groups: [], total: 0 };
  const keys = [...asArray(oci.groups).map((g) => g.key), ...asArray(poci.groups).map((g) => g.key)]
    .filter((k, i, a) => a.indexOf(k) === i);
  const lineKey = (l) => l.code || l.key;
  return (
    <div data-testid="pl-oci">
      <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
        {t("ifrs.a18.oci")}
        <span className="ms-2 normal-case font-normal text-muted-foreground">{t("ifrs.a18.ociNetOfTax")}</span>
      </div>
      {keys.map((k) => {
        const g = asArray(oci.groups).find((x) => x.key === k);
        const pg = asArray(poci.groups).find((x) => x.key === k);
        const head = g || pg;
        const lines = [...asArray(g?.lines), ...asArray(pg?.lines).filter((pl) => !asArray(g?.lines).some((l) => lineKey(l) === lineKey(pl)))];
        return (
          <div key={k} data-testid={`pl-oci-group-${k}`}>
            <div className="border-b px-4 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
              {(lang === "ar" && head.label_ar) || head.label}
            </div>
            {lines.map((l) => {
              const cur = asArray(g?.lines).find((x) => lineKey(x) === lineKey(l));
              const pr = asArray(pg?.lines).find((x) => lineKey(x) === lineKey(l));
              return (
                <div key={lineKey(l)} className="grid grid-cols-2 border-b px-4 py-1.5 text-sm" data-testid={`pl-oci-line-${lineKey(l)}`}>
                  <div className="ps-4 text-slate-700 dark:text-slate-300">
                    {l.code && <span className="me-2 font-mono text-[10px] text-muted-foreground">{l.code}</span>}
                    {(lang === "ar" && l.name_ar) || l.name}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-end num-cell">
                    <div>{fmt(cur?.amount || 0)}</div>
                    <div className="text-muted-foreground">{fmt(pr?.amount || 0)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {subtotal(t("ifrs.a18.ociTotal"), oci.total || 0, poci.total || 0, "pl-oci-total")}
      {subtotal(t("ifrs.a18.tci"),
                Number(current?.profit_for_period || 0) + Number(oci.total || 0),
                Number(prior?.profit_for_period || 0) + Number(poci.total || 0), "pl-tci")}
    </div>
  );
}

// A9: "first year of IFRS 18" — shown on the notes of an IFRS 18 engagement; team levels 1 and 2 can change it
// (Batch 32 · F1: the server's rule, ifrs18_transition.can_set — engagement_team_rules.level_of(role) in (1, 2); Admin excluded)
export const FIRST_YEAR_ROLES = new Set(["Partner", "Audit Manager", "Manager", "Senior Manager", "Assistant Manager", "Reviewer",
                                         "Supervisor"]);

export function Ifrs18FirstYearToggle({ engagement, framework, onSaved }) {
  const { t } = useI18n();
  const { user } = useAuth() || {};
  const [value, setValue] = useState(!!engagement?.ifrs18_first_year);
  const [busy, setBusy] = useState(false);
  if (framework !== "IFRS 18" || !engagement) return null;
  const canSet = FIRST_YEAR_ROLES.has(user?.role);    // Batch 32 · F1: team levels 1 and 2 (as the server)
  const change = async (next) => {
    setBusy(true);
    try {
      await api.put(`/engagements/${engagement.id}/ifrs18-first-year`, { first_year: next });
      setValue(next);
      toast.success(t("ifrs.a18.firstYear.saved"));
      if (onSaved) onSaved(next);
    } catch (e) {
      toast.error(toErrMsg(e, t("ifrs.a18.firstYear.saveFailed")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-start gap-2 border-b px-4 py-2 text-xs" data-testid="ifrs18-first-year">
      <label className="inline-flex items-center gap-2 font-medium">
        <input type="checkbox" checked={value} disabled={!canSet || busy}
               onChange={(e) => change(e.target.checked)} data-testid="ifrs18-first-year-checkbox" />
        {t("ifrs.a18.firstYear.label")}
      </label>
      <span className="text-muted-foreground">{t("ifrs.a18.firstYear.help")}</span>
      {!canSet && <span className="text-muted-foreground italic" data-testid="ifrs18-first-year-roles">{t("ifrs.a18.firstYear.rolesOnly")}</span>}
    </div>
  );
}

// A9: the transition note — prior-year lines (IAS 1 heading, IFRS 18 category) and the subtotals side by side
export function TransitionNote({ note }) {
  const { t, lang } = useI18n();
  const lineCols = useResizableColumns({ storageKey: "ifrs18-transition-lines", defaults: [300, 200, 200, 130], mins: [160, 120, 120, 100] });
  const subCols = useResizableColumns({ storageKey: "ifrs18-transition-subtotals", defaults: [300, 140, 140, 140], mins: [160, 100, 100, 100] });
  if (!note) return null;
  const tr = note.transition;
  if (!tr) {
    return <p className="mt-3 text-[11px] text-muted-foreground" data-testid="ifrs18-transition-empty">
      {(lang === "ar" && note.message_ar) || note.message}</p>;
  }
  const L = (l, k) => (lang === "ar" && l[`${k}_ar`]) || l[k];
  const na = t("ifrs.a18.transition.notApplicable");
  return (
    <div className="mt-3 space-y-3" data-testid="ifrs18-transition">
      {/* PR 46 review · A12: the IAS 1 column is rebuilt from today's mapping, not the published statements */}
      {note.basis && <p className="text-[11px] text-muted-foreground" data-testid="ifrs18-transition-basis">
        {(lang === "ar" && note.basis_ar) || note.basis}</p>}
      <ResizableTable cols={lineCols} wrap className="w-full text-xs">
        <thead>
          <tr className="text-start text-[10px] uppercase tracking-wider text-muted-foreground">
            <ResizableTh index={0} className="py-1 pe-2">{t("ifrs.a18.transition.line")}</ResizableTh>
            <ResizableTh index={1} className="py-1 pe-2">{t("ifrs.a18.transition.ias1")}</ResizableTh>
            <ResizableTh index={2} className="py-1 pe-2">{t("ifrs.a18.transition.ifrs18")}</ResizableTh>
            <ResizableTh index={3} className="py-1 pe-2 text-end">{t("ifrs.a18.transition.amount")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {asArray(tr.lines).map((l) => (
            <tr key={`${l.code}${l.fx_tag || ""}`} className="border-t border-slate-200/60 dark:border-slate-800/60"
                data-testid={`ifrs18-transition-line-${l.code}${l.fx_tag ? `-${l.fx_tag}` : ""}`}>
              <td className="py-1 pe-2"><span className="me-1 font-mono text-[10px] text-muted-foreground">{l.code}</span>{L(l, "name")}</td>
              <td className="py-1 pe-2">{L(l, "ias1_heading")}</td>
              <td className="py-1 pe-2">{L(l, "ifrs18_category")}</td>
              <td className="py-1 pe-2 text-end num-cell">{fmt(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
      <ResizableTable cols={subCols} wrap className="w-full text-xs">
        <thead>
          <tr className="text-start text-[10px] uppercase tracking-wider text-muted-foreground">
            <ResizableTh index={0} className="py-1 pe-2">{t("ifrs.a18.transition.subtotal")}</ResizableTh>
            <ResizableTh index={1} className="py-1 pe-2 text-end">{t("ifrs.a18.transition.ias1Col")}</ResizableTh>
            <ResizableTh index={2} className="py-1 pe-2 text-end">{t("ifrs.a18.transition.ifrs18Col")}</ResizableTh>
            <ResizableTh index={3} className="py-1 pe-2 text-end">{t("ifrs.a18.transition.difference")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {asArray(tr.subtotals).map((s) => (
            <tr key={s.key} className="border-t border-slate-200/60 dark:border-slate-800/60" data-testid={`ifrs18-transition-sub-${s.key}`}>
              <td className="py-1 pe-2 font-medium">{L(s, "label")}</td>
              <td className="py-1 pe-2 text-end num-cell">{s.ias1 == null ? na : fmt(s.ias1)}</td>
              <td className="py-1 pe-2 text-end num-cell">{s.ifrs18 == null ? na : fmt(s.ifrs18)}</td>
              <td className="py-1 pe-2 text-end num-cell">{s.difference == null ? "" : fmt(s.difference)}</td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
    </div>
  );
}
